import * as Yup from 'yup';
import REGEX from '../../../Constants/Regex';

const MB = 1024 * 1024;

const PROFILE_FILE_SIZE = 2;
const BANNER_FILE_SIZE = 5;
const AADHAR_CARD_FILE_SIZE = 3;
const PREVIOUS_CERTIFICATES_FILE_SIZE = 10;
const DIGITAL_SIGNATURE_FILE_SIZE = 1;

const FILE_SIZES = {
    PROFILE_FILE_SIZE,
    BANNER_FILE_SIZE,
    EDUCATIONAL_CERTIFICATE: BANNER_FILE_SIZE,
    AADHAR_CARD_FILE_SIZE,
    PREVIOUS_CERTIFICATES_FILE_SIZE,
    DIGITAL_SIGNATURE_FILE_SIZE
};

const _getFileSize = (size = 0) => size * MB;

const IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const PDF_TYPES = ["application/pdf"];

// const pathname = window.location.pathname;
// const params = new URLSearchParams(pathname);
// const id = params.get("trainer_id");

// const isEdit = pathname.includes("my-profile") && !!id;

const isFileType = (file, types) => !!file && types.includes(file.type);

/** Helper — check file with document fallback */
const fileValidation = (label, allowedTypes, sizeLimit, isPdf = false) => {
    // helper to get id from URL (either path like /my-profile/123 or query ?id=123)
    const getIdFromUrl = () => {
        if (typeof window === 'undefined') return null;
        const segments = window.location.pathname.split("/");
        return !isNaN(segments?.[3]) ? segments[3] : null;
    };

    return Yup.mixed()
        .test(`${label}-required`, `${label} is required`, function (file) {
            const { documents } = this.parent;

            // 1) if backend provided document → not required
            if (!file && !!documents && documents[label]) return true;

            // 2) if user is on /my-profile/:id (or ?id=) → not required
            const idFromUrl = getIdFromUrl();
            const isOnMyProfile = typeof window !== 'undefined' && window.location.pathname.includes('/my-profile');

            if (isOnMyProfile || idFromUrl) return true;

            // 3) otherwise file is required
            return !!file;
        })
        .test(`${label}-type`, isPdf ? "Only PDF files are allowed" : "Only images are allowed (jpg, png, jpeg, webp)", function (file) {
            if (!file) return true; // nothing to validate (handled by required test)
            return isFileType(file, allowedTypes);
        })
        .test(`${label}-size`, `${label} must be ≤ ${sizeLimit} MB`, function (file) {
            if (!file) return true;
            return file.size <= _getFileSize(sizeLimit);
        });
};

const youtubeLinkItemSchema = Yup.object().shape({
    link: Yup.string()
        .trim()
        .test("is-youtube-url", "Invalid YouTube URL", (value) =>
            !value ? true : REGEX.YOUTUBE_REGEX.test(value)
        ),
    title: Yup.string().when("link", {
        is: (val) => !!val,
        then: (schema) => schema.required("Title is required when URL is provided"),
        otherwise: (schema) => schema.optional(),
    }),
    channel: Yup.string().optional(),
    views: Yup.string().optional(),
});

// SINGLE SCHEMA FOR CREATE + EDIT
const validationSchema = Yup.object({
    // normal required fields
    first_name: Yup.string().required('First name is required').min(2, 'Minimum 2 characters'),
    last_name: Yup.string().required('Last name is required').min(2, 'Minimum 2 characters'),
    user_name: Yup.string()
        .transform(value => {
            if (typeof value !== "string") return value;
            // force lowercase and remove invalid chars
            return value.toLowerCase().replace(/[^a-z1-9_]/g, "");
        })
        .required("Username is required")
        .min(3, "Type at least 3 characters")
        .matches(/^[a-z1-9_]+$/, "Only lowercase letters (a–z), digits (1–9) and underscore (_) allowed"),
    dob: Yup.string().required('Date of birth is required'),
    gender: Yup.string().required('Gender is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phone_no: Yup.string().matches(/^[0-9]{10}$/, 'Phone number must be 10 digits').required(),
    designation: Yup.string().required(),
    whatsapp_no: Yup.string().matches(/^[0-9]{10}$/, 'WhatsApp number must be 10 digits').required(),
    full_address: Yup.string().required(),
    city_id: Yup.string().required(),
    pincode: Yup.string().matches(/^[0-9]{6}$/, 'Pincode must be 6 digits').required(),
    state_id: Yup.string().required(),
    country_id: Yup.string().required(),
    instagram: Yup.string().url("Must be a valid URL").nullable().optional(),
    facebook: Yup.string().url("Must be a valid URL").nullable().optional(),
    youtube: Yup.string().url("Must be a valid URL").nullable().optional(),
    linkedin: Yup.string().url("Must be a valid URL").nullable().optional(),
    tagline: Yup.string().required().max(100, "Maximum 100 characters"),
    about_description: Yup.string().required().min(50, "Minimum 50 characters"),
    experience_yrs: Yup.string().required(),
    clients_coached: Yup.number().min(0).required(),
    workplace_name: Yup.string().required(),
    qualification: Yup.string().required(),
    languages: Yup.array().min(1).required(),
    certification_tags: Yup.array().min(1).required(),
    specialization_tags: Yup.array().min(1).required(),
    services: Yup.array().min(1).required(),
    youtube_links: Yup.array().of(youtubeLinkItemSchema).optional(),
    codeOfEthics: Yup.boolean().oneOf([true], 'You must agree to the Code of Ethics'),

    /** FILES WITH CREATE + EDIT DUAL LOGIC */
    documents: Yup.object().optional(), // backend documents object

    profile_img: fileValidation("profile_img", IMAGE_TYPES, PROFILE_FILE_SIZE),
    banner_img: fileValidation("banner_img", IMAGE_TYPES, BANNER_FILE_SIZE),
    aadhar_card: fileValidation("aadhar_card", PDF_TYPES, AADHAR_CARD_FILE_SIZE, true),
    educational_certificate: fileValidation("educational_certificate", PDF_TYPES, BANNER_FILE_SIZE, true),
    previous_certificates: fileValidation("previous_certificates", PDF_TYPES, PREVIOUS_CERTIFICATES_FILE_SIZE, true),

    // previous_certificates: Yup.mixed()
    //     .test("previous-cert-required", "Certificate is required", function (file) {
    //         const { documents } = this.parent;
    //         if (documents && documents.previous_certificates?.length > 0) return true;
    //         return !!file;
    //     })
    //     .test("previous-cert-type", "Only PDF files are allowed", (file) =>
    //         !file ? true : isFileType(file, PDF_TYPES)
    //     )
    //     .test("previous-cert-size", `Max size ≤ ${PREVIOUS_CERTIFICATES_FILE_SIZE} MB`, (file) =>
    //         !file ? true : file.size <= _getFileSize(PREVIOUS_CERTIFICATES_FILE_SIZE)
    //     ),

    digital_signature: fileValidation("digital_signature", IMAGE_TYPES, DIGITAL_SIGNATURE_FILE_SIZE)
});

export { validationSchema, FILE_SIZES };
